import { inject, Injectable, OnDestroy } from '@angular/core';
import { Filters, FilterField } from './filters/filter';
import { ActivatedRoute } from '@angular/router';
import { Observable, BehaviorSubject, Subscription } from 'rxjs';
import { filter, skipWhile, map } from 'rxjs/operators';
import { URLParamService } from '../url-param.service';
import { LocalStorageService } from '../local-storage.service';
import { ProductAnalyticsService } from '@vendasta/product-analytics';

export interface SavedFilter {
  name: string;
  serializedFields: string | null;
  expiry: number | null;
}
const LAST_APPLIED_FIELDS = '__last_applied_fields__';
const EXPIRY_LAST_APPLIED_FIELDS = 30 * 60 * 1000; // 30 min expiry time for last applied fields

@Injectable()
export class FilterService implements OnDestroy {
  public filters: Filters;
  /*
    What: lazyLoadedFields are a mechanism for the consumer of the service to tell the filter service that these
    filter fields may not immediately have their `option` values. In knowing this, the filter service can subscribe
    to those fields changing options, and once the options have all been filled, apply the filter values
    Why: Since many of our filter fields can take a list of `any` type of object, we need to serialize those
    objects into a much more consistent and concise representation in the url/storage. Upon deserializing these
    values, we need to check to see they are still valid, and map back to an original object, based on the filter field options.
    Since many fields do not have their options loaded when this service is instantiated, we can keep this list
    of fields, and wait for their options to load before we deserialize the url/storage values.
    */
  public lazyLoadedFields: FilterField<any>[] = [];
  private initializationParams: any = {};
  private fieldSubscription: Subscription;
  private lazyFieldSubscription: Subscription;
  private _savedFilters = new BehaviorSubject<SavedFilter[] | null>(null);
  /*
    What: batchChangedFields is a local list of fields that the service is currently updating. Once complete, the service will
    emit a new list of values through the fieldValueChanges observable, and this value will be flushed.
    Why:  When changing several fields at once, we don't want to trigger the consumer (a component using the service)
    to make an ajax request for each changed field. By batching value changes, and emiting them all at once, we can allow
    the consumer to intelligently cascade many updates at a time to the data it is filtering.
    */
  private batchChangedFields: FilterField<any>[] = [];
  private _fieldValueChanges = new BehaviorSubject<FilterField<any>[] | null>(null);
  private tracking = inject(ProductAnalyticsService);

  constructor(
    private route: ActivatedRoute,
    private paramService: URLParamService,
    private localStorageService: LocalStorageService,
  ) {}

  public setFilters(allFilters: Filters, lazyLoadedFields?: FilterField<any>[], disableSavedFilters?: boolean): void {
    this.filters = allFilters;
    if (!this.filters) {
      return;
    }
    this.fieldSubscription = this.filters.fieldValuesObservable.subscribe((field: FilterField<any>) => {
      this.updateUrl();
      this.saveCurrentFilter(LAST_APPLIED_FIELDS, Date.now() + EXPIRY_LAST_APPLIED_FIELDS);
      if (!this.isFieldIncludedInBatch(field)) {
        this._fieldValueChanges.next([field]);
      }
    });
    if (lazyLoadedFields) {
      this.setLazyLoadedFields(lazyLoadedFields);
    }

    const params = this.getFilterValuesFromUrl(this.filters);

    const existingSavedFilters = this.localStorageService.get(this.filters.id);
    if (existingSavedFilters && !disableSavedFilters) {
      const savedFilters = JSON.parse(existingSavedFilters);
      this.trackFilters(savedFilters);
      this._savedFilters.next(savedFilters);
    }

    this.initializationParams = params || JSON.parse(this.lastAppliedFields.serializedFields!) || null;
    if (this.initializationParams) {
      this.updateFilterFields(this.filters.fields, this.initializationParams);
    }

    this.updateUrl();
  }

  public setLazyLoadedFields(lazyLoadedFields: FilterField<any>[]): void {
    this.lazyLoadedFields = lazyLoadedFields;
    this.lazyFieldSubscription = this.filters.fieldOptionsObservable
      .pipe(filter((field) => this.lazyLoadedFields.some((lazyField) => lazyField === field)))
      .subscribe((field) => this.trackLazyLoadedFieldOptionChanges(field, this.lazyLoadedFields));
  }

  public get savedFilters(): Observable<SavedFilter[]> {
    return this._savedFilters.asObservable().pipe(
      skipWhile((value: any) => value === null),
      map((savedFilters: SavedFilter[]) => {
        return savedFilters.filter((savedFilter: SavedFilter) => savedFilter.name !== LAST_APPLIED_FIELDS);
      }),
    );
  }

  public get fieldValueChanges(): Observable<FilterField<any>[] | null> {
    return this._fieldValueChanges.asObservable().pipe(skipWhile((value) => value === null));
  }

  private trackFilters(savedFilters: SavedFilter[]): void {
    if (!this.tracking.getVisitorId()) {
      return;
    }
    const filterNames = savedFilters
      .map((savedFilter: SavedFilter) => JSON.parse(savedFilter.serializedFields ?? '{}'))
      .map((f) => Object.keys(f))
      .flat();
    for (const name of filterNames) {
      this.tracking.trackEvent('legacy-ui-filters', 'table-filters-v2', 'changed', 1, {
        filterName: name,
      });
    }
  }

  public saveCurrentFilter(name: string, expiry: number | null = null): boolean {
    if (!name) {
      return false;
    }
    const currentlyAppliedFields = this.serializeFilters();
    const savedFilters = this._savedFilters.getValue() || [];
    const existingSavedFilterIndex = savedFilters.findIndex((savedFilter) => savedFilter.name === name);
    if (existingSavedFilterIndex !== -1) {
      savedFilters[existingSavedFilterIndex] = {
        name: name,
        serializedFields: currentlyAppliedFields,
        expiry: expiry,
      };
    } else {
      savedFilters.push({
        name: name,
        serializedFields: currentlyAppliedFields,
        expiry: expiry,
      });
    }

    const success = this.localStorageService.set(this.filters.id, JSON.stringify(savedFilters));
    if (success) {
      this.trackFilters(savedFilters);
      this._savedFilters.next(savedFilters);
      return true;
    } else {
      return false;
    }
  }

  public removeSavedFilter(name: string): boolean {
    const savedFilters = this._savedFilters.getValue() || [];
    const existingSavedFilterIndex = savedFilters.findIndex((savedFilter) => savedFilter.name === name);
    if (existingSavedFilterIndex !== -1) {
      savedFilters.splice(existingSavedFilterIndex, 1);
      const success = this.localStorageService.set(this.filters.id, JSON.stringify(savedFilters));
      if (success) {
        this._savedFilters.next(savedFilters);
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  public applySavedFilter(savedFilter: SavedFilter): void {
    const paramValues = JSON.parse(savedFilter.serializedFields!);
    this.updateFilterFields(this.filters.fields, paramValues);
  }

  public getFilterValuesFromUrl(filters: Filters): any {
    const params = this.route.snapshot.queryParams;
    if (!params[filters.id]) {
      return null;
    }
    return JSON.parse(decodeURIComponent(params[filters.id]));
  }

  public updateUrl(): void {
    const serializedFilters = this.serializeFilters();
    if (serializedFilters !== '{}') {
      const filterParam = {};
      filterParam[this.filters.id] = serializedFilters;
      this.paramService.applyParams(filterParam);
    } else {
      this.paramService.removeParams([this.filters.id]);
    }
  }

  private isFieldIncludedInBatch(field: FilterField<any>): boolean {
    return this.batchChangedFields.some((changedField) => changedField === field);
  }

  private get lastAppliedFields(): SavedFilter {
    const savedFilters = this._savedFilters.getValue() || [];
    let lastAppliedFields: SavedFilter | undefined | null = savedFilters.find(
      (savedFilter) => savedFilter.name === LAST_APPLIED_FIELDS,
    );
    if (lastAppliedFields && lastAppliedFields.expiry && lastAppliedFields.expiry < Date.now()) {
      this.removeSavedFilter(LAST_APPLIED_FIELDS);
      lastAppliedFields = null;
    }
    const emptyFilterSelection: SavedFilter = { name: LAST_APPLIED_FIELDS, serializedFields: null, expiry: null };
    return lastAppliedFields || emptyFilterSelection;
  }

  private trackLazyLoadedFieldOptionChanges(field: FilterField<any>, lazyLoadedFields: FilterField<any>[]): void {
    const indexOfField = lazyLoadedFields.findIndex((f) => f === field);
    lazyLoadedFields.splice(indexOfField, 1);
    if (lazyLoadedFields.length === 0 && this.initializationParams) {
      const fieldsToUpdate = this.filters.fields.filter((f) => this.initializationParams[f.id]);
      this.updateFilterFields(fieldsToUpdate, this.initializationParams);
      this.initializationParams = null;
    }
  }

  private serializeFilters(): any {
    return JSON.stringify(this.serializeAllActiveFields());
  }

  private serializeAllActiveFields(): object {
    return this.filters.activeFields.reduce((previous, current) => {
      previous[current.id] = current.serializeValue();
      return previous;
    }, {});
  }

  private updateFilterFields(fieldsToUpdate: FilterField<any>[], paramValues: any): void {
    fieldsToUpdate.forEach((field, index, fieldArray) => {
      const key = field.id;
      // If no paramValue present for field, clear the value
      const newValue = paramValues[key] ? field.deserializeValue(paramValues[key]) : null;
      const publishChanges = index === fieldArray.length - 1;
      this.updateFilterField(field, newValue, publishChanges);
    });
  }

  private updateFilterField(field: FilterField<any>, newValue: any, publishChanges: boolean): void {
    this.batchChangedFields.push(field);
    if (newValue === null) {
      field.clearValue();
    } else if (field.value !== newValue) {
      field.changeValue(newValue);
    }
    if (publishChanges) {
      this._fieldValueChanges.next(this.batchChangedFields);
      this.batchChangedFields = [];
    }
  }

  public exportFilterString(): string {
    return this.serializeFilters();
  }

  public importFilterFields(importString: string): void {
    const paramValues = JSON.parse(importString);
    this.updateFilterFields(this.filters.fields, paramValues);
  }

  ngOnDestroy(): void {
    if (this.filters) {
      this.paramService.removeParams([this.filters.id]);
    }
    if (this.fieldSubscription) {
      this.fieldSubscription.unsubscribe();
    }
    if (this.lazyFieldSubscription) {
      this.lazyFieldSubscription.unsubscribe();
    }
  }
}
